import type { RiskPictureDay, RiskPictureItem } from "./risk-picture-model";

/** Shared by the table, the map, and the map sidebar. */
export interface RiskPictureSelection {
  dayIndex: number;
  /** Set only while the sidebar shows one warning. */
  itemId?: string;
}

export const selectRiskPictureDay = (dayIndex: number): RiskPictureSelection => ({
  dayIndex,
});

export const selectRiskPictureWarning = (
  dayIndex: number,
  itemId: string,
): RiskPictureSelection => ({ dayIndex, itemId });

export const clearRiskPictureWarning = (
  selection: RiskPictureSelection,
): RiskPictureSelection => ({ dayIndex: selection.dayIndex });

export type RiskPictureSidebarView = "details" | "calendar" | "closed";

/**
 * The calendar (day list) and a warning's details are independent; they only share the
 * panel, and details win. So closing details reveals the calendar only if it was open.
 */
export const riskPictureSidebarView = (
  calendarOpen: boolean,
  hasDetails: boolean,
): RiskPictureSidebarView => {
  if (hasDetails) return "details";
  return calendarOpen ? "calendar" : "closed";
};

/** Only the timeline toggles its already-selected warning off on a second click. */
export const riskPictureTimelineClickAction = (
  itemId: string,
  selectedItemId: string | undefined,
): "back" | "select" => (itemId === selectedItemId ? "back" : "select");

/**
 * Day the timeline's day tabs move to for a key, per the WAI-ARIA tabs pattern:
 * ←/→ step one day and stop at the ends (no wrap, so the ends are announced as reached),
 * Home/End jump to the first/last day. `undefined` for any other key, so it is not handled.
 */
export const riskPictureDayTabKeyTarget = (
  key: string,
  dayIndex: number,
  dayCount: number,
): number | undefined => {
  if (dayCount <= 0) return undefined;
  const last = dayCount - 1;
  switch (key) {
    case "ArrowLeft":
      return Math.max(dayIndex - 1, 0);
    case "ArrowRight":
      return Math.min(dayIndex + 1, last);
    case "Home":
      return 0;
    case "End":
      return last;
    default:
      return undefined;
  }
};

/** Hover previews a day without changing the user's persistent selection. */
export const riskPictureMapPreview = (
  days: readonly RiskPictureDay[],
  selectedDayIndex: number,
  hovered: RiskPictureSelection | undefined,
): RiskPictureSelection =>
  hovered?.itemId &&
  days[hovered.dayIndex]?.items.some((item) => item.id === hovered.itemId)
    ? hovered
    : { dayIndex: selectedDayIndex };

/** A hovered warning remains visible even when map filters hide its category. */
export const riskPicturePreviewItems = (
  dayItems: RiskPictureItem[],
  filteredItems: RiskPictureItem[],
  previewItemId: string | undefined,
): RiskPictureItem[] => {
  const previewItem = dayItems.find((item) => item.id === previewItemId);
  return previewItem && !filteredItems.includes(previewItem)
    ? [...filteredItems, previewItem]
    : filteredItems;
};

/**
 * Selection excludes unrelated markers before clustering. The selected item remains a
 * member so the marker renderer can suppress its hit target (polygon-only details).
 * An explicit timeline/calendar preview takes precedence, even through map filters.
 */
export const riskPictureMarkerItems = (
  dayItems: RiskPictureItem[],
  filteredItems: RiskPictureItem[],
  selectedItemId?: string,
  previewItemId?: string,
): RiskPictureItem[] => {
  const selectedItem = dayItems.find((item) => item.id === selectedItemId);
  if (selectedItem) {
    const previewItem = dayItems.find((item) => item.id === previewItemId);
    return [previewItem ?? selectedItem];
  }
  return riskPicturePreviewItems(dayItems, filteredItems, previewItemId);
};

/** The selected warning plus a distinct hover preview; otherwise every item of the day. */
export const riskPictureDrawnItems = (
  dayItems: RiskPictureItem[],
  itemId: string | undefined,
  previewItemId?: string,
): RiskPictureItem[] => {
  if (!itemId) return dayItems;
  const item = dayItems.find((candidate) => candidate.id === itemId);
  if (!item) return dayItems;
  const preview = dayItems.find((candidate) => candidate.id === previewItemId);
  return preview && preview !== item ? [item, preview] : [item];
};
